import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';
import { incomeService } from '../services/incomeService';

function Income() {
  const [incomeSources, setIncomeSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newSource, setNewSource] = useState({
    name: '',
    type: 'cash',
    balance: 0
  });
  const [incomes, setIncomes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingIncome, setEditingIncome] = useState(null);
  const [newIncome, setNewIncome] = useState({
    sourceType: 'Salary', // Default value
    sourceName: '',
    sourceDetails: '',
    amount: '',
    date: new Date().toISOString().split('T')[0]
  });

  const sourceTypes = [
    'Salary',
    'Freelance',
    'Investment',
    'Business',
    'Rental',
    'Other'
  ];

  useEffect(() => {
    fetchIncomeSources();
  }, []);

  const fetchIncomeSources = async () => {
    try {
      const response = await incomeService.getIncomes();
      setIncomeSources(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch income sources');
      console.error('Error fetching income sources:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!newIncome.sourceType || !newIncome.sourceName || !newIncome.sourceDetails) {
        alert('Please fill in all required fields');
        return;
      }

      const response = await incomeService.createIncome(newIncome);
      setIncomes([...incomes, response.data]);
      setNewIncome({
        sourceType: 'Salary',
        sourceName: '',
        sourceDetails: '',
        amount: '',
        date: new Date().toISOString().split('T')[0]
      });
      setShowForm(false);
    } catch (error) {
      console.error('Error adding income:', error);
      alert(error.response?.data?.error || 'Failed to add income');
    }
  };

  const handleDelete = async (id) => {
    try {
      await incomeService.deleteIncome(id);
      setIncomeSources(incomeSources.filter(source => source._id !== id));
    } catch (error) {
      console.error('Error deleting income:', error);
    }
  };

  const handleEdit = async (id) => {
    if (!editingIncome) {
      // If not editing, set the income to edit mode
      const incomeToEdit = incomes.find(income => income._id === id);
      setEditingIncome(incomeToEdit);
      return;
    }
    
    try {
      const response = await incomeService.updateIncome(id, editingIncome);
      if (response.data) {
        setIncomes(incomes.map(income => 
          income._id === id ? response.data : income
        ));
        setEditingIncome(null); // Clear editing state
      }
    } catch (error) {
      console.error('Error updating income:', error);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-3xl font-bold mb-8">Income Sources</h1>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4">Add New Income Source</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <select
            value={newIncome.sourceType}
            onChange={(e) => setNewIncome({...newIncome, sourceType: e.target.value})}
            className="w-full p-2 border rounded"
            required
          >
            <option value="">Select Source Type</option>
            {sourceTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Source Name"
            value={newIncome.sourceName}
            onChange={(e) => setNewIncome({...newIncome, sourceName: e.target.value})}
            className="w-full p-2 border rounded"
            required
          />
          <textarea
            placeholder="Source Details"
            value={newIncome.sourceDetails}
            onChange={(e) => setNewIncome({...newIncome, sourceDetails: e.target.value})}
            className="w-full p-2 border rounded"
            required
          />
          <input
            type="number"
            placeholder="Amount"
            value={newIncome.amount}
            onChange={(e) => setNewIncome({...newIncome, amount: e.target.value})}
            className="w-full p-2 border rounded"
            required
          />
          <input
            type="date"
            value={newIncome.date}
            onChange={(e) => setNewIncome({...newIncome, date: e.target.value})}
            className="w-full p-2 border rounded"
            required
          />
          <button 
            type="submit"
            disabled={loading}
            className={`px-4 py-2 rounded-md text-white transition-colors ${
              loading 
                ? 'bg-blue-300 cursor-not-allowed' 
                : 'bg-blue-500 hover:bg-blue-600'
            }`}
          >
            {loading ? 'Adding...' : 'Add Source'}
          </button>
        </form>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4">Your Income Sources</h2>
        {loading && <p className="text-gray-500">Loading income sources...</p>}
        {!loading && incomeSources.length === 0 ? (
          <p className="text-gray-500">No income sources added yet.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {incomeSources.map(source => (
              <div key={source.id} className="bg-gray-50 p-4 rounded-lg border">
                <h3 className="text-lg font-semibold mb-2">{source.name}</h3>
                <p className="text-gray-600 mb-1">Type: {source.type}</p>
                <p className="text-gray-600">Balance: ${source.balance}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Income;
